import React from 'react';
import {
  Box,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Popover,
  Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PaymentRow, listPayments, markPaymentAsPaid } from '../api/payments';
import { listUsers } from '../api/users';

const formatDate = (date?: Date) =>
  date ? date.toLocaleDateString() : '—';

const formatAmount = (amount?: number) =>
  amount != null ? `${amount.toFixed(0)} CFA` : '—';

const statusColor = (status?: string) => {
  switch (status) {
    case 'paid':
      return 'success';
    case 'pending':
    default:
      return 'warning';
  }
};

export const PaymentsPage: React.FC = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery<PaymentRow[]>({
    queryKey: ['payments'],
    queryFn: () => listPayments(),
  });

  const { data: users } = useQuery({
    queryKey: ['users-for-payments'],
    queryFn: listUsers,
  });

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<string>('all');
  const [orderBy, setOrderBy] = React.useState<
    'amount' | 'status' | 'createdAt' | 'updatedAt' | 'payer'
  >('createdAt');
  const [order, setOrder] = React.useState<'asc' | 'desc'>('desc');

  const [confirmAnchorEl, setConfirmAnchorEl] =
    React.useState<HTMLElement | null>(null);
  const [confirmDescription, setConfirmDescription] = React.useState('');
  const [pendingPayment, setPendingPayment] = React.useState<PaymentRow | null>(
    null
  );
  const confirmOpen = Boolean(confirmAnchorEl);

  const handleRequestSort = (
    property: 'amount' | 'status' | 'createdAt' | 'updatedAt' | 'payer'
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const userNameById = React.useMemo(() => {
    const map: Record<string, string> = {};
    users?.forEach((u) => {
      const fullName = `${u.lastName ?? ''} ${u.firstName ?? ''}`.trim();
      map[u.id] = fullName || u.email || u.id;
    });
    return map;
  }, [users]);

  const filtered = React.useMemo(() => {
    if (!data) return [];
    return data.filter((p) => {
      const payer = p.paidById ? userNameById[p.paidById] ?? p.paidById : '';
      const haystack = `${payer} ${p.method ?? ''}`.toLowerCase().trim();
      const matchesSearch = haystack.includes(search.toLowerCase());
      const matchesStatus =
        statusFilter === 'all' || (p.status ?? 'pending') === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [data, search, statusFilter, userNameById]);

  const sorted = React.useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const factor = order === 'asc' ? 1 : -1;
      let av: string | Date | number | undefined;
      let bv: string | Date | number | undefined;

      const getPayer = (x: PaymentRow) =>
        x.paidById ? userNameById[x.paidById] ?? x.paidById : '';

      switch (orderBy) {
        case 'payer':
          av = getPayer(a).toLowerCase();
          bv = getPayer(b).toLowerCase();
          break;
        case 'status':
          av = (a.status ?? '').toLowerCase();
          bv = (b.status ?? '').toLowerCase();
          break;
        case 'amount':
          av = a.amount ?? 0;
          bv = b.amount ?? 0;
          break;
        case 'createdAt':
          av = a.createdAt ?? new Date(0);
          bv = b.createdAt ?? new Date(0);
          break;
        case 'updatedAt':
          av = a.updatedAt ?? new Date(0);
          bv = b.updatedAt ?? new Date(0);
          break;
        default:
          av = 0 as any;
          bv = 0 as any;
      }

      if (av instanceof Date && bv instanceof Date) {
        return (av.getTime() - bv.getTime()) * factor;
      }

      const as = String(av);
      const bs = String(bv);
      if (as < bs) return -1 * factor;
      if (as > bs) return 1 * factor;
      return 0;
    });
    return arr;
  }, [filtered, order, orderBy, userNameById]);

  const paginated = React.useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  const handleMarkPaidRequest = (
    event: React.MouseEvent<HTMLElement>,
    payment: PaymentRow
  ) => {
    const payer = payment.paidById
      ? userNameById[payment.paidById] ?? payment.paidById
      : 'cet utilisateur';
    const amountLabel = formatAmount(payment.amount);

    setConfirmDescription(
      `Confirmer le paiement de ${amountLabel} pour ${payer} ?`
    );
    setPendingPayment(payment);
    setConfirmAnchorEl(event.currentTarget);
  };

  const handleConfirmPaid = async () => {
    if (!pendingPayment) return;
    await markPaymentAsPaid(pendingPayment.id);
    await queryClient.invalidateQueries({ queryKey: ['payments'] });
    setConfirmAnchorEl(null);
    setPendingPayment(null);
  };

  const handleCancelPaid = () => {
    setConfirmAnchorEl(null);
    setPendingPayment(null);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>
        Paiements
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 2,
        }}
      >
        <TextField
          size="small"
          label="Rechercher"
          placeholder="Payeur, méthode..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="status-filter-label">Statut</InputLabel>
          <Select
            labelId="status-filter-label"
            label="Statut"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">Tous</MenuItem>
            <MenuItem value="pending">En attente</MenuItem>
            <MenuItem value="paid">Payé</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === 'payer' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'payer'}
                  direction={orderBy === 'payer' ? order : 'asc'}
                  onClick={() => handleRequestSort('payer')}
                >
                  Payeur
                </TableSortLabel>
              </TableCell>
              <TableCell>Méthode</TableCell>
              <TableCell sortDirection={orderBy === 'amount' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'amount'}
                  direction={orderBy === 'amount' ? order : 'asc'}
                  onClick={() => handleRequestSort('amount')}
                >
                  Montant
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'status' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleRequestSort('status')}
                >
                  Statut
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'createdAt' ? order : false}
              >
                <TableSortLabel
                  active={orderBy === 'createdAt'}
                  direction={orderBy === 'createdAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('createdAt')}
                >
                  Créé le
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === 'updatedAt' ? order : false}
              >
                <TableSortLabel
                  active={orderBy === 'updatedAt'}
                  direction={orderBy === 'updatedAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('updatedAt')}
                >
                  Mis à jour le
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={7}>Chargement...</TableCell>
              </TableRow>
            )}
            {!isLoading &&
              paginated.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>
                    {p.paidById
                      ? userNameById[p.paidById] ?? p.paidById
                      : '—'}
                  </TableCell>
                  <TableCell>{p.method ?? '—'}</TableCell>
                  <TableCell>{formatAmount(p.amount)}</TableCell>
                  <TableCell>
                    <Chip
                      label={p.status ?? 'pending'}
                      color={statusColor(p.status) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(p.createdAt)}</TableCell>
                  <TableCell>{formatDate(p.updatedAt)}</TableCell>
                  <TableCell align="right">
                    {p.status !== 'paid' && (
                      <Tooltip title="Marquer comme payé">
                        <IconButton
                          size="small"
                          onClick={(event) => handleMarkPaidRequest(event, p)}
                        >
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            {!isLoading && !filtered.length && (
              <TableRow>
                <TableCell colSpan={7}>
                  <Typography variant="body2" color="text.secondary">
                    Aucun paiement trouvé.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={filtered.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
      />
      <Popover
        open={confirmOpen}
        anchorEl={confirmAnchorEl}
        onClose={handleCancelPaid}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 2, maxWidth: 280 }}>
          <Typography variant="subtitle2" gutterBottom>
            Marquer le paiement comme payé
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {confirmDescription}
          </Typography>
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1,
            }}
          >
            <Button onClick={handleCancelPaid} color="inherit" size="small">
              Annuler
            </Button>
            <Button
              onClick={handleConfirmPaid}
              color="primary"
              variant="contained"
              size="small"
            >
              Confirmer
            </Button>
          </Box>
        </Box>
      </Popover>
    </Paper>
  );
};

